import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { toast } from '../../../../ui/toast';
import { Button } from '../../../../ui';
import { createJob, getJob } from '../../../../utils/api_service/job_api';
import { formatDateTime } from '../../../../utils/helpers/time';
import {
  Settings,
  TopicBasedGeneratorJob,
} from '../../../../utils/types/content_manager_types';
import { GenerateProperties } from '../GenerateProperties/GenerateProperties';
import { QuestionsList } from '../question_list';
import { SessionTitle } from '../session_title';
import { TopicInput } from '../topic_input';

export const TopicQuestionGenerate: React.FC = () => {
  const questionsCount = useRef(0);
  const [topic, setTopic] = useState<string>('');
  const [defaultSessionTitle, setDefaultSessionTitle] = useState<string>(
    `${topic ? topic : ''}-`,
  );
  const [sessionTitle, setSessionTitle] = useState<string>(defaultSessionTitle);
  const [questionsLoading, setQuestionsLoading] = useState<boolean>(false);
  const [errorJob, setErrorJob] = useState<boolean>(false);

  useEffect(() => {
    const currentDateTimeString = formatDateTime(new Date());
    const titlePrefix = topic ? topic : 'Question generation';
    setDefaultSessionTitle(`${titlePrefix} - ${currentDateTimeString}`);
  }, [topic]);

  const [editGenerateProperties, setEditGenerateProperties] =
    useState<Settings>({
      level: '',
      jobTitle: '',
      questionType: '',
      time: 0,
      points: 0,
    });
  const [job, setJob] = useState<TopicBasedGeneratorJob>({
    id: '',
    generationType: '',
    _cls: '',
    title: '',
    organizationId: '',
    status: 'Uploading...',
    creationDateTime: 0,
    topic: '',
    questions: [],
  });

  const pollJob = (id: string) => {
    getJob(id)
      .then((response) => {
        setJob(response);
        if (response.status && response.status.includes('Error')) {
          setQuestionsLoading(false);
          setErrorJob(true);
          toast.error(`${response.status} Please Try Again`, {
            position: 'bottom-center',
          });
        } else {
          questionsCount.current = response.questions?.length || 0;
          if (questionsCount.current === 0) {
            setTimeout(() => {
              pollJob(id);
            }, 5000);
          } else {
            setQuestionsLoading(false);
          }
        }
      })
      .catch(() => {
        toast.error('Error. Please try again', { position: 'bottom-center' });
        setErrorJob(true);
      });
  };

  const generateQuestions = () => {
    setQuestionsLoading(true);
    setErrorJob(false);
    createJob(sessionTitle, topic, 'topic', editGenerateProperties).then(
      (response) => {
        if (response.questions && response.questions.length !== 0) {
          setJob(response);
          setQuestionsLoading(false);
        } else {
          pollJob(response.id);
        }
      },
    );
  };

  return (
    <Stack spacing={2}>
      <SessionTitle
        sessionTitle={sessionTitle}
        setSessionTitle={setSessionTitle}
        defaultValue={defaultSessionTitle}
        disabled={
          questionsLoading || (job.questions && job.questions.length > 0)
        }
      />
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Topic Skills
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Add a main skill and optional sub-skills to guide generation.
          </Typography>
        </Box>
        <TopicInput
          setTopic={setTopic}
          disabled={
            questionsLoading || (job.questions && job.questions.length > 0)
          }
          placeholder="Type a skill and press enter..."
        />
      </Paper>
      <GenerateProperties
        setEditGenerateProperties={setEditGenerateProperties}
        job={job}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() => generateQuestions()}
          variant="primary"
          label="Generate Questions"
          disabled={
            editGenerateProperties.jobTitle === '' ||
            topic === '' ||
            sessionTitle === '' ||
            editGenerateProperties.level === '' ||
            (job.questions && job.questions.length > 0) ||
            questionsLoading
          }
        />
      </Box>
      <Box>
        <QuestionsList
          job={job}
          questionsLoading={questionsLoading}
          setJob={setJob}
          errorJob={errorJob}
        />
      </Box>
    </Stack>
  );
};
